import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import WComponent from "../../../../../common/WComponent";
import StepTrace from "../review/checklist/tools/StepTrace";
import DocumentViewerModal from "../../shared/DocumentViewerModal";

/**
 * Read-only drilldown for an Agent Mode check (passed or failing). Mirrors the
 * Review tab's ExecutionSteps visual style — same .afs-review__exec-* classes
 * — but renders a static manifest snapshot rather than driving live prompts /
 * AJE approvals. The Review tab owns interactivity; Agent Mode owns the
 * narrative of what already happened.
 *
 * Props:
 *   r          — final_check_results entry (id, passed, label, detail,
 *                resolution {type, suggestion}, execution_step_statuses,
 *                supporting, documents, passed_reason, passed_label)
 *   decisions  — full manifest.decisions array (filtered internally to this check)
 *   onNavigateToDocumentation, onNavigateToReview, onNavigateToClientRequests
 */
class AgentCheckDrilldown extends WComponent {
	state = {
		showCompleted: true,
		viewerDoc: null,
	};

	openDocViewer = (doc) => { this.update({ viewerDoc: doc || null }); };
	closeDocViewer = () => { this.update({ viewerDoc: null }); };

	render() {
		let { r, decisions, onNavigateToClientRequests } = this.props;
		let { showCompleted, viewerDoc } = this.state;
		let openDocViewer = this.openDocViewer;
		let closeDocViewer = this.closeDocViewer;

		let allSteps = Array.isArray(r.execution_step_statuses) ? r.execution_step_statuses : [];
		let relevantSteps = allSteps.filter(function (s) { return s.status !== "not_applicable"; });
		let completedSteps = relevantSteps.filter(function (s) { return s.status === "completed"; });
		let activeSteps = relevantSteps.filter(function (s) { return s.status !== "completed"; });

		let checkDecisions = (decisions || []).filter(function (d) { return d.check_id === r.id; });
		let pendingClientReqs = checkDecisions.filter(function (d) { return d.decision_type === "client_request_created"; }).length;
		let supportingEntries = [];
		if (r.supporting && typeof r.supporting === "object") {
			for (let k of Object.keys(r.supporting)) {
				let v = r.supporting[k];
				if (v == null) continue;
				let label = k.replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
				let value;
				if (typeof v === "boolean") {
					value = v ? "Yes" : "No";
				} else if (typeof v === "number") {
					if (/total|amount|balance|sum|expense|income|capital|assets|liabilities/i.test(k)) {
						let abs = Math.abs(v).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
						value = (v < 0 ? "-$" : "$") + abs;
					} else {
						value = v.toLocaleString("en-CA");
					}
				} else if (Array.isArray(v)) {
					value = v.length === 0 ? "—" : v.join(", ");
				} else if (typeof v === "object") {
					value = JSON.stringify(v);
				} else {
					value = String(v);
				}
				supportingEntries.push({ label: label, value: value });
			}
		}

		let resultDocs = Array.isArray(r.documents) ? r.documents : [];
		let comparisons = Array.isArray(r.document_comparisons) ? r.document_comparisons : [];
		let verdict = r.passed_reason || r.passed_label || null;
		let resolutionChip = r.resolution && (r.resolution.type || r.resolution.suggestion) ? (r.resolution.type || "").replace(/_/g, " ").toUpperCase() : null;

		let statusLevel = function (status) {
			if (status === "match") return "match";
			if (status === "variance") return "variance";
			if (status === "missing_pdf") return "missing";
			return "default";
		};
		let statusIcon = function (status) {
			if (status === "match") return Icon.CheckCircle;
			if (status === "variance") return Icon.AlertCircle;
			if (status === "missing_pdf") return Icon.HelpCircle;
			return Icon.Minus;
		};
		let statusLabel = function (status, deltaDisplay) {
			if (status === "match") return "Match";
			if (status === "variance") return deltaDisplay ? "Δ " + deltaDisplay : "Variance";
			if (status === "missing_pdf") return "Not in PDF";
			return "—";
		};

		let typeLabel = function (t) {
			if (t === "aje_auto_accepted") return "AJE";
			if (t === "reclassification_auto_accepted") return "Reclassification";
			if (t === "client_request_created") return "Client Request";
			if (t === "acknowledgement") return "Analysis";
			return t;
		};

		// Split a step detail into a one-line summary + a list of finding rows.
		// Detail strings from the registry use the convention "summary:\nfinding1\nfinding2..."
		// for multi-finding steps (e.g. BS_01's sign-anomaly check produces 7 lines,
		// one per anomalous account). Single-line details have no findings.
		let parseStepDetail = function (detail) {
			if (!detail) return { summary: null, findings: [] };
			let lines = detail.split("\n").map(function (l) { return l.trim(); }).filter(function (l) { return l.length > 0; });
			if (lines.length <= 1) return { summary: lines[0] || null, findings: [] };
			return { summary: lines[0], findings: lines.slice(1) };
		};

		return (
			<div className="afs-review__exec-steps agent-mode__drilldown">
				{/* Resolution chip + suggestion (mirrors the Review tab header line) */}
				{r.resolution && r.resolution.suggestion && (
					<div className="afs-review__exec-resolution-line">
						<span className="agent-mode__resolution-key">Resolution:</span>
						<span className="agent-mode__resolution-text">{r.resolution.suggestion}</span>
						{resolutionChip && <span className="afs-review__resolution-type">{resolutionChip}</span>}
					</div>
				)}

				{/* Verdict (passed_reason or passed_label) when the check is passing */}
				{r.passed && verdict && (
					<div className="agent-mode__verdict">
						<Icon.CheckCircle size={14} className="agent-mode__verdict-icon" />
						<span>{verdict}</span>
					</div>
				)}

				{/* Per-document PDF-vs-GL comparison tables. Lets the practitioner
				    verify what the parser extracted matches the books in one glance
				    instead of clicking through to the Documentation tab. */}
				{comparisons.map(function (cmp, ci) {
					return (
						<div key={ci} className="agent-mode__doc-compare">
							<div className="agent-mode__doc-compare-title">
								<Icon.FileText size={12} />
								<span>{cmp.title || "Document vs General Ledger"}</span>
								{cmp.document_name && (
									<button
										type="button"
										className="agent-mode__doc-link agent-mode__doc-link--inline"
										onClick={function (ev) { ev.stopPropagation(); openDocViewer({ id: cmp.document_id, name: cmp.document_name, doc_type: cmp.document_type, source: "evidence_item" }); }}
										title="View source document"
									>
										<Icon.FileText size={11} className="agent-mode__doc-link-icon" />
										{cmp.document_name}
									</button>
								)}
							</div>
							<div className="agent-mode__reclass-table">
								<div className="t__header agent-mode__reclass-header">
									<div className="t__header__item agent-mode__cmp-field">Field</div>
									<div className="t__header__item agent-mode__cmp-pdf">From PDF</div>
									<div className="t__header__item agent-mode__cmp-books">On the books</div>
									<div className="t__header__item agent-mode__cmp-status">Status</div>
								</div>
								{(cmp.rows || []).map(function (row, ri) {
									let odd = ri % 2 === 0;
									let StatusIcon = statusIcon(row.status);
									let level = statusLevel(row.status);
									return (
										<div key={ri} className={clsx("t__row", odd && "t__row--stripe")} title={row.hint || undefined}>
											<div className="t__row__item agent-mode__cmp-field">{row.label}</div>
											<div className="t__row__item agent-mode__cmp-pdf">{row.from_pdf && row.from_pdf.display}</div>
											<div className="t__row__item agent-mode__cmp-books">{row.from_books && row.from_books.display}</div>
											<div className={clsx("t__row__item", "agent-mode__cmp-status", "agent-mode__cmp-status--" + level)}>
												<StatusIcon size={11} />
												<span className="agent-mode__cmp-status-text">{statusLabel(row.status, row.delta_display)}</span>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					);
				})}

				{/* Execution-step trace — mirrors Review tab's "All N steps completed" */}
				{relevantSteps.length > 0 && (
					<>
						<div
							className={clsx("afs-review__exec-summary", activeSteps.length === 0 && "afs-review__exec-summary--done")}
							onClick={() => { this.update({ showCompleted: !showCompleted }); }}
						>
							<Icon.CheckCircle size={14} color={activeSteps.length === 0 ? "#2e7d32" : "#888"} />
							<span>
								{activeSteps.length === 0
									? "All " + completedSteps.length + " step" + (completedSteps.length === 1 ? "" : "s") + " completed"
									: completedSteps.length + " of " + relevantSteps.length + " steps completed"}
							</span>
							<span className="afs-review__exec-summary-toggle">{showCompleted ? "hide" : "show"}</span>
						</div>
						{showCompleted && (
							<div className="afs-review__exec-completed-steps">
								{relevantSteps.map(function (step) {
									let isCompleted = step.status === "completed";
									let parsed = parseStepDetail(step.detail);
									let hasFindings = parsed.findings.length > 0;
									return (
										<div key={step.id} className={clsx("agent-mode__step-item", hasFindings && "agent-mode__step-item--multi")}>
											<div className="agent-mode__step-row">
												{isCompleted
													? <Icon.Check size={12} color="#2e7d32" className="agent-mode__step-icon" />
													: <Icon.AlertCircle size={12} color="#e6a117" className="agent-mode__step-icon" />}
												<span className={clsx("agent-mode__step-label", isCompleted && "agent-mode__step-label--done")}>{step.label}</span>
												{parsed.summary && <span className="agent-mode__step-summary">— {parsed.summary}</span>}
												<StepTrace trace={step.trace} />
											</div>
											{hasFindings && (
												<ul className="agent-mode__step-findings">
													{parsed.findings.map(function (line, fi) {
														// Each finding line typically follows the pattern
														// "Account Name (balance) — diagnosis [— recommendation]".
														// Split on em-dash to highlight the leading account /
														// label, then indent the remainder.
														let parts = line.split(" — ");
														let head = parts.length > 1 ? parts[0] : null;
														let body = parts.length > 1 ? parts.slice(1).join(" — ") : line;
														return (
															<li key={fi} className="agent-mode__step-finding">
																{head ? (
																	<>
																		<span className="agent-mode__step-finding-head">{head}</span>
																		<span className="agent-mode__step-finding-body"> — {body}</span>
																	</>
																) : (
																	<span className="agent-mode__step-finding-body">{body}</span>
																)}
															</li>
														);
													})}
												</ul>
											)}
										</div>
									);
								})}
							</div>
						)}
					</>
				)}

				{/* Agent decisions tied to this check (RJEs, mappings, acknowledgements,
				    client requests). Renders a chip + summary or analysis bullets, same
				    as the Need Review card already does — keeps the agent's contribution
				    visible alongside the system's step trace. */}
				{checkDecisions.length > 0 && (
					<div className="agent-mode__agent-did">
						<div className="agent-mode__progress-line agent-mode__progress-line--gap">
							<Icon.Check size={14} color="#2e7d32" className="agent-mode__progress-icon" />
							<span className="agent-mode__progress-label">Agent did</span>
						</div>
						{!r.passed && pendingClientReqs > 0 && onNavigateToClientRequests && (
							<div className="agent-mode__progress-line agent-mode__progress-line--gap">
								<Icon.AlertCircle size={14} color="#e6a117" className="agent-mode__progress-icon" />
								<span className="agent-mode__progress-label">Action needed</span>
								<span className="agent-mode__progress-action">
									Awaiting client response —{" "}
									<button
										type="button"
										className="agent-mode__progress-link"
										onClick={function (e) { e.stopPropagation(); onNavigateToClientRequests(); }}
									>see Client Requests</button>
								</span>
							</div>
						)}
						<div className="agent-mode__progress-actions agent-mode__progress-actions--flush">
							{checkDecisions.map(function (d, k) {
								let docs = Array.isArray(d.documents) ? d.documents : [];
								let isAnalysis = d.decision_type === "acknowledgement" && Array.isArray(d.evidence) && d.evidence.length >= 2;
								let isClientReq = d.decision_type === "client_request_created";
								let chipClickable = isClientReq && onNavigateToClientRequests;
								let txList = d.transaction_reclassifications || [];
								let fmtAmount = function (n) {
									let v = parseFloat(n) || 0;
									let abs = Math.abs(v).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
									return v < 0 ? "-$" + abs : "$" + abs;
								};
								return (
									<div key={k} className="agent-mode__progress-action-item">
										<div className="agent-mode__progress-action-row">
											<span
												className={clsx("afs-review__resolution-type", "agent-mode__progress-chip", chipClickable && "agent-mode__progress-chip--clickable")}
												onClick={chipClickable ? function (e) { e.stopPropagation(); onNavigateToClientRequests(); } : undefined}
												title={chipClickable ? "Open Client Requests" : undefined}
											>{typeLabel(d.decision_type)}</span>
											{!isAnalysis && txList.length === 0 && <span className="agent-mode__progress-action-summary">{d.detail}</span>}
										</div>
										{isAnalysis && (
											<ul className="agent-mode__analysis-list">
												{(d.evidence || []).map(function (e, ei) {
													return (
														<li key={ei} className="agent-mode__analysis-item">
															<span className="agent-mode__analysis-label">{e.step}</span>
															<span className="agent-mode__analysis-value">{e.result}</span>
														</li>
													);
												})}
												{docs.length > 0 && (
													<li className="agent-mode__analysis-item">
														<span className="agent-mode__analysis-label">Source documents</span>
														<span className="agent-mode__analysis-value">
															{docs.map(function (doc, di) {
																return (
																	<button
																		key={di}
																		type="button"
																		className="agent-mode__doc-link"
																		onClick={function (ev) { ev.stopPropagation(); openDocViewer(doc); }}
																		title="View source document"
																	>
																		<Icon.FileText size={11} className="agent-mode__doc-link-icon" />
																		{doc.name || doc.doc_type}
																	</button>
																);
															})}
														</span>
													</li>
												)}
											</ul>
										)}
										{txList.length > 0 && (
											<div className="agent-mode__reclass-table agent-mode__reclass-table--spaced">
												<div className="t__header agent-mode__reclass-header">
													<div className="t__header__item agent-mode__rc-date">Date</div>
													<div className="t__header__item agent-mode__rc-desc">Description</div>
													<div className="t__header__item agent-mode__rc-to">Reclassified to</div>
													<div className="t__header__item agent-mode__rc-amount">Amount</div>
													<div className="t__header__item agent-mode__rc-conf">Confidence</div>
												</div>
												{txList.map(function (t, ti) {
													let odd = ti % 2 === 0;
													return (
														<div key={ti} className={clsx("t__row", odd && "t__row--stripe")}>
															<div className="t__row__item agent-mode__rc-date">{t.date || "—"}</div>
															<div className="t__row__item agent-mode__rc-desc">{t.description}</div>
															<div className="t__row__item agent-mode__rc-to">{t.to_display || t.to_code}</div>
															<div className="t__row__item agent-mode__rc-amount">{fmtAmount(t.amount)}</div>
															<div className="t__row__item agent-mode__rc-conf">{t.confidence != null ? Math.round(t.confidence * 100) + "%" : "—"}</div>
														</div>
													);
												})}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Supporting facts + source documents (when there's no execution-step
				    trace, this is the only structured detail available) */}
				{(supportingEntries.length > 0 || resultDocs.length > 0) && relevantSteps.length === 0 && (
					<ul className="agent-mode__analysis-list agent-mode__analysis-list--spaced">
						{supportingEntries.map(function (e, k) {
							return (
								<li key={k} className="agent-mode__analysis-item">
									<span className="agent-mode__analysis-label">{e.label}</span>
									<span className="agent-mode__analysis-value">{e.value}</span>
								</li>
							);
						})}
						{resultDocs.length > 0 && (
							<li className="agent-mode__analysis-item">
								<span className="agent-mode__analysis-label">Source documents</span>
								<span className="agent-mode__analysis-value">
									{resultDocs.map(function (doc, di) {
										return (
											<button
												key={di}
												type="button"
												className="agent-mode__doc-link"
												onClick={function (ev) { ev.stopPropagation(); openDocViewer(doc); }}
												title="View source document"
											>
												<Icon.FileText size={11} className="agent-mode__doc-link-icon" />
												{doc.name || doc.doc_type}
											</button>
										);
									})}
								</span>
							</li>
						)}
					</ul>
				)}
				<DocumentViewerModal
					open={!!viewerDoc}
					onClose={closeDocViewer}
					doc={viewerDoc}
				/>
			</div>
		);
	}
}

export default AgentCheckDrilldown;
